"use client";

import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// The public shell header. It is rendered on the landing scene and on every
// standalone public page, so both share one link set, one wordmark and one
// scroll behaviour instead of drifting apart.

export type SiteHeaderProps = {
  // "landing" fades the header away over the hero; "page" keeps it docked.
  variant?: "landing" | "page";
  // Path of the current page, used to mark its own nav entry.
  active?: string;
  authenticated?: boolean;
  studioHref?: string;
};

const LINK_CLASS =
  "font-mono text-[10px] uppercase tracking-[0.2em] md:tracking-[0.35em] whitespace-nowrap no-underline transition-colors";

// Pricing sits between the paper and the telemetry: it is the question a
// visitor asks after "what is this" and before "is it running".
const NAV_LINKS = [
  { label: "[ WHITE_PAPER ]", href: "https://files.cestus.network/whitepaper.pdf", external: true },
  { label: "[ PRICING ]", href: "/pricing", external: false },
  { label: "[ NETWORK_PULSE ]", href: "/network-pulse", external: false }
];

// Breakpoint the burger swaps at, matching Tailwind's `md`. The nav links are
// nowrap by design, and below this they cannot all fit on one line.
const NAV_BREAKPOINT = "(min-width: 768px)";

function CestusWordmark() {
  return (
    <svg
      viewBox="0 0 1309 170"
      // The mark is 7.7:1, so height drives width. Keep the same compact
      // geometry on the landing page and standalone public pages.
      className="h-5 w-auto fill-current text-white transition-colors duration-300 ease-out md:h-10"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Cestus Network"
    >
      <g>
        <path d="M354.8,75.4h-39.2c2.1-17.4,9.7-34.2,23.1-47.6l1.5-1.5c31.6-31.6,82.7-31.6,114.3,0l0.3,0.3l-27.1,27.1
        c-17.1-16.6-44.3-16.5-61.2,0.4C360.4,60.2,356.6,67.7,354.8,75.4z M427.7,116.2l27.1,27.1l-0.3,0.3c-31.6,31.6-82.7,31.6-114.3,0
        l-1.5-1.5c-13.3-13.3-21-30.2-23.1-47.6h39.2c1.7,7.8,5.6,15.2,11.7,21.3C383.4,132.7,410.6,132.8,427.7,116.2L427.7,116.2z"/>
        <path d="M614.7,48.1h-95.3v76.5h95.3v38.2H481.1V9.9h133.6V48.1z M538.5,105.5V67.3h57.1v38.2H538.5z"/>
        <path d="M773.3,48.1H639.7c0-21.1,17.1-38.2,38.2-38.2h95.3V48.1z M792.4,115v0.2c0,26.4-21.4,47.7-47.7,47.7h-105
        v-38.2h104.9c5.3,0,9.6-4.3,9.6-9.6c0-5.3-4.3-9.6-9.6-9.6h-66.7c-21.1,0-38.2-17.1-38.2-38.2h105C771,67.3,792.4,88.6,792.4,115z"/>
        <path d="M799.2,48.2V10h152.7v38.2H799.2z M856.4,162.8V67.3h38.2v95.5H856.4z"/>
        <path d="M1009,100.4c0,15.8,12.8,28.7,28.7,28.7v38.1h-0.2c-36.9,0-66.8-29.9-66.8-66.8V9.5h38.2V100.4z M1123.8,9.5
        v90.9c0,36.9-29.9,66.8-66.8,66.8h-0.2V129c15.8,0,28.7-12.8,28.7-28.7V9.5H1123.8z"/>
        <path d="M1284.1,48.1h-133.6c0-21.1,17.1-38.2,38.2-38.2h95.3V48.1z M1303.2,115v0.2c0,26.4-21.4,47.7-47.7,47.7h-105
        v-38.2h104.9c5.3,0,9.6-4.3,9.6-9.6c0-5.3-4.3-9.6-9.6-9.6h-66.7c-21.1,0-38.2-17.1-38.2-38.2h105
        C1281.9,67.3,1303.2,88.6,1303.2,115z"/>
      </g>
      <g>
        <path d="M117,54v62H55v54h49.8c36.6,0,66.2-29.6,66.2-66.2V54H117z"/>
        <path d="M0,66.2V116h55V54h62V0H66.2C29.6,0,0,29.6,0,66.2"/>
      </g>
    </svg>
  );
}

export default function SiteHeader({
  variant = "landing",
  active,
  authenticated = false,
  studioHref = "/studio"
}: SiteHeaderProps) {
  const [ isScrolled, setIsScrolled ] = useState(false);
  const [ headerFade, setHeaderFade ] = useState(1);
  const [ menuOpen, setMenuOpen ] = useState(false);
  const fades = variant === "landing";

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;

      setIsScrolled(y >= 25);
      if (!fades) return;

      // Fade out header between 0px → 70% viewport height
      const fadeEnd = window.innerHeight * 0.7;
      setHeaderFade(1 - Math.min(Math.max(y / fadeEnd, 0), 1));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ fades ]);

  // Growing past the breakpoint reveals the inline links, so the panel holding
  // the same links has to go with it.
  useEffect(() => {
    const query = window.matchMedia(NAV_BREAKPOINT);
    const close = () => setMenuOpen(false);

    query.addEventListener("change", close);
    return () => query.removeEventListener("change", close);
  }, []);

  // Scroll is locked while the panel is open, which also keeps the landing
  // variant from fading the header out from under it.
  useEffect(() => {
    if (!menuOpen) return;

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeydown);
    };
  }, [ menuOpen ]);

  const openWalletModal = () => {
    setMenuOpen(false);
    window.dispatchEvent(new CustomEvent("swarm:open-wallet-modal"));
  };

  const navLink = ({ label, href, external }: (typeof NAV_LINKS)[number], className: string) => {
    const current = active === href;

    return (
      <a
        key={href}
        aria-current={current ? "page" : undefined}
        className={`${LINK_CLASS} ${className} ${
          current
            ? "text-blue-300 [text-shadow:0_0_18px_rgba(56,189,248,0.55)]"
            : "text-white/90 hover:text-blue-300 focus-visible:text-blue-300"
        }`}
        href={href}
        onClick={() => setMenuOpen(false)}
        rel={external ? "noopener noreferrer" : undefined}
        target={external ? "_blank" : undefined}
      >
        {label}
      </a>
    );
  };

  const authAction = (className: string) =>
    authenticated ? (
      <a
        className={`${LINK_CLASS} ${className} text-blue-200 hover:text-blue-300 focus-visible:text-blue-300`}
        href={studioHref}
        onClick={() => setMenuOpen(false)}
      >
        [ STUDIO ]
      </a>
    ) : (
      <button
        className={`${LINK_CLASS} ${className} cursor-pointer text-white/90 hover:text-blue-300 focus-visible:text-blue-300`}
        onClick={openWalletModal}
        type="button"
      >
        [ SIGN IN ]
      </button>
    );

  return (
    <motion.nav
      // The panel hangs off this bar, so the bar must not fade out under it.
      style={fades && !menuOpen ? { opacity: headerFade, y: headerFade < 1 ? -10 * (1 - headerFade) : 0 } : undefined}
      transition={{ ease: "easeOut", duration: 0.2 }}
      className={`fixed top-0 left-0 z-50 flex w-full items-center justify-between border-b px-4 py-4 pointer-events-none transition-[background-color,border-color,backdrop-filter] duration-300 md:px-8 md:py-5 ${
        isScrolled || menuOpen
          ? "border-blue-400/15 bg-[#050b13]/85 backdrop-blur-md"
          : "border-transparent bg-transparent"
      }`}
    >
      <a className="pointer-events-auto" href="/" aria-label="Cestus Network home">
        <CestusWordmark />
      </a>

      <div className="pointer-events-auto hidden md:flex items-center gap-6">
        <div className="text-[10px] font-mono tracking-widest text-blue-400 uppercase whitespace-nowrap">
          [ STATUS: OPERATIONAL ]
        </div>

        {NAV_LINKS.map((link) => navLink(link, ""))}
        {authAction("")}
      </div>

      {/* Square, hairline, no rounding: the same instrument vocabulary as the
          bracketed labels it stands in for. */}
      <button
        aria-controls="site-header-menu"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? "Close navigation" : "Open navigation"}
        className="pointer-events-auto md:hidden grid place-content-center gap-[5px] w-10 h-10 cursor-pointer
          border border-blue-400/30 bg-blue-500/5 transition-colors hover:border-blue-400/60 focus-visible:border-blue-400/60"
        onClick={() => setMenuOpen((open) => !open)}
        type="button"
      >
        <span
          className={`block w-4 h-px bg-white/85 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-in-out)] ${
            menuOpen ? "translate-y-[6px] rotate-45" : ""
          }`}
        />
        <span className={`block w-4 h-px bg-white/85 transition-opacity duration-[var(--duration-fast)] ease-[var(--ease-in-out)] ${menuOpen ? "opacity-0" : ""}`} />
        <span
          className={`block w-4 h-px bg-white/85 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-in-out)] ${
            menuOpen ? "-translate-y-[6px] -rotate-45" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            // The close is the quicker half of the pair - it gets out of the way.
            exit={{ opacity: 0, y: -8, transition: { duration: 0.15 } }}
            id="site-header-menu"
            initial={{ opacity: 0, y: -8 }}
            transition={{ ease: [ 0.22, 1, 0.36, 1 ], duration: 0.25 }}
            className="pointer-events-auto md:hidden absolute top-full left-0 w-full
              border-b border-blue-400/15 bg-[#050b13]/97 backdrop-blur-md"
          >
            <div className="flex flex-col px-4">
              {NAV_LINKS.map((link) =>
                navLink(link, "flex items-center h-14 border-t border-blue-400/10 tracking-[0.25em]")
              )}
              {authAction("flex items-center h-14 border-t border-blue-400/10 tracking-[0.25em] text-left bg-transparent border-x-0 border-b-0")}
              <p className="flex items-center h-11 m-0 border-t border-blue-400/10 font-mono text-[9px] tracking-[0.25em] text-blue-400/70 uppercase">
                [ STATUS: OPERATIONAL ]
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
